import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBlogPosts, BlogPost } from '../services/blog';
import AnimatedSection from './AnimatedSection';
import { useViewTracking } from '../hooks/useViewTracking';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({ category: '' });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 12, pages: 1 });

  useViewTracking({ viewType: 'blog' });

  const loadBlogPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBlogPosts({
        category: filters.category || undefined,
        limit: pagination.limit,
        page: pagination.page,
      });
      setBlogPosts(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [filters.category, pagination.limit, pagination.page]);

  useEffect(() => {
    loadBlogPosts();
  }, [loadBlogPosts]);

  const handlePostClick = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <section id="blog" className="py-20 brand-bg relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <AnimatedSection animation="slide-up">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-dark-900 dark:text-neutral-50 mb-6">
              Our <span className="text-primary-500">Blog</span>
            </h2>
            <p className="text-xl text-dark-600 dark:text-neutral-300 max-w-3xl mx-auto leading-relaxed">
              Insights, updates, and stories from our team
            </p>
          </div>
        </AnimatedSection>

        {/* Filters */}
        <AnimatedSection animation="fade-in" delay={100}>
          <div className="mb-8 flex justify-center">
            <input
              type="text"
              value={filters.category}
              onChange={(e) => {
                setFilters({ category: e.target.value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              placeholder="Filter by category..."
              className="px-6 py-3 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-800 text-dark-900 dark:text-neutral-50 max-w-md w-full focus:outline-none focus:border-primary-500"
            />
          </div>
        </AnimatedSection>

        {/* Blog Posts Grid */}
        <AnimatedSection animation="fade-in" delay={200}>
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-dark-600 dark:text-neutral-300">Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg p-4 text-center">
              {error}
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-12 border border-primary-500/20 text-center">
              <p className="text-dark-600 dark:text-neutral-300 text-lg">No blog posts found.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {blogPosts.map((post) => (
                  <div
                    key={post._id}
                    onClick={() => handlePostClick(post._id)}
                    className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl overflow-hidden border border-primary-500/20 hover:border-primary-500/40 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    {post.imageUrl && (
                      <div className="w-full min-h-48 max-h-64 overflow-hidden bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          className="w-full h-auto max-h-64 object-contain transition-transform duration-300 hover:scale-110"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-dark-500 dark:text-neutral-400">
                        {post.category && (
                          <span className="px-2 py-1 bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded text-xs whitespace-nowrap">
                            {post.category}
                          </span>
                        )}
                        <span className="whitespace-nowrap">{formatDate(post.publishedAt)}</span>
                      </div>
                      <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-dark-600 dark:text-neutral-300 mb-4 line-clamp-3">
                        {truncateContent(post.content)}
                      </p>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <span className="text-sm text-dark-500 dark:text-neutral-400 flex items-center gap-1 whitespace-nowrap">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>{post.views} views</span>
                        </span>
                        <span className="text-primary-500 font-medium text-sm whitespace-nowrap">Read More →</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-dark-600 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Blog;

